import '@maps/leaflet-global'; // must precede any leaflet plugin import
import { UNITED_STATES_BOUNDS_LITERAL } from '@miq/shared-models';
import * as L from 'leaflet';
import 'leaflet-draw';
/**
 * L2-S1/T7: every PURE declaration in this file moved to @miq/shared-models
 * (maps/esri-map.types.ts) so the reports remote's `map-view.component`, which needs only the
 * `LMapOptions` interface, stops importing a module whose side effects bootstrap the leaflet-draw
 * plugin. This re-export keeps the old path working; new code should import from
 * '@miq/shared-models'.
 *
 * What stays here is exactly what CANNOT move: the two `L.latLngBounds` constants, which are built
 * by calling into leaflet at module-evaluation time.
 *
 * L2-S6: the US box's four COORDINATES now live in @miq/shared-models as the plain-array
 * `UNITED_STATES_BOUNDS_LITERAL` and `UNITED_STATES_BOUNDS` is built from it, so the numbers are
 * stated once. The constant's RUNTIME TYPE is deliberately unchanged — it must stay a real
 * `L.LatLngBounds`, because `@records/shared/state/search-state.constants` seeds
 * `DEFAULT_MAP_STATE.currentBounds` with it and `search-state.helpers`' `getViewPortPolygon` /
 * `makeLatLngBounds` read leaflet's PRIVATE `_northEast` / `_southWest` fields off it. Handing them
 * a plain array would silently produce an `undefined` default geo-polygon filter.
 */
export {
  GeoPolygonType,
  LeafletShape,
  LeafletPosition,
  SearchMode,
  DEFAULT_MAP_OPTIONS,
  MetricConversion,
  leafLetOptionsFactory,
  UNITED_STATES_BOUNDS_LITERAL,
} from '@miq/shared-models';
export type {
  LatLong,
  GeoPolygon,
  LMapOptions,
  CardViewDataModel,
  CardFactoryFn,
  EntityInfoColloquialModel,
} from '@miq/shared-models';
/* CONSTANTS */
const southWest = L.latLng(-89.98155760646617, -180);
const northEast = L.latLng(89.99346179538875, 180);
export const WORLD_BOUNDS = L.latLngBounds(southWest, northEast);
// Built from the shared literal (see the L2-S6 note above). The two-argument `latLngBounds(sw, ne)`
// form is kept verbatim, so `_southWest` / `_northEast` are assigned exactly as before.
const [usSouthWestLiteral, usNorthEastLiteral] = UNITED_STATES_BOUNDS_LITERAL;
const usSouthWest = L.latLng(usSouthWestLiteral);
const usNorthEast = L.latLng(usNorthEastLiteral);
export const UNITED_STATES_BOUNDS = L.latLngBounds(usSouthWest, usNorthEast);
